package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initZavarnikPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("zavarnik.md", FrontMatterElement.Builder { addScalar("title", "zavarnik"); addScalar("searchTitle", "Gradle plugin for Project Leyden AOT cache — train, verify, ship for Ktor and plain JVM apps"); addScalar("description", "Trains a Leyden AOT cache through the application plugin's real start script, fails the build when the JVM would reject it or when the JDK would not even look, and ships it inside distTar and the container image."); addScalar("library", "zavarnik"); addScalar("post", "/blog/leyden-aot-cache-for-a-plain-kotlin-jvm-service"); }))
}

@Page("/zavarnik")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun ZavarnikPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Project Leyden (JDK 25 and 26) makes a JVM service ready two to three times faster from an AOT cache, with no change to the code. Spring Boot and Quarkus wrapped the workflow into their own tooling; a Ktor server, an http4k service or a CLI on the plain ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("application")
        }
        org.jetbrains.compose.web.dom.Text(" plugin has two commands and a list of ways the cache stops working without telling anyone. The JVM rejects a cache silently by default, exit code 0 and three lines on stderr. Four builds of JDK 25 and two of JDK 26 do not compare the cache with the jars at all (")
        com.varabyte.kobweb.silk.components.navigation.Link("https://bugs.openjdk.org/browse/JDK-8377932") {
            org.jetbrains.compose.web.dom.Text("JDK-8377932")
        }
        org.jetbrains.compose.web.dom.Text("), so a rebuilt jar runs last week's classes. A ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-jre")
        }
        org.jetbrains.compose.web.dom.Text(" image refuses a cache trained on the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-jdk")
        }
        org.jetbrains.compose.web.dom.Text(" image of the same build. A zip loses the timestamps the check depends on. Each of these was measured before the plugin was written; the research document and the logs are in the repository.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("aotTrain")
                }
            }
            org.jetbrains.compose.web.dom.Text(" runs the installed distribution through its own start script with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-XX:AOTCacheOutput")
            }
            org.jetbrains.compose.web.dom.Text(", waits for a readiness URL, runs a workload of HTTP requests or commands, stops the JVM with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SIGTERM")
            }
            org.jetbrains.compose.web.dom.Text(" and waits for the cache to be assembled. Jar timestamps are pinned to the constant Gradle's tar uses, so the cache survives ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("distTar")
            }
            org.jetbrains.compose.web.dom.Text(" and Docker ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("COPY")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("aotVerify")
                }
            }
            org.jetbrains.compose.web.dom.Text(", on ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("check")
            }
            org.jetbrains.compose.web.dom.Text(", is the point. It compares the jars with a SHA-256 manifest written at training time, starts the application under ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-XX:AOTMode=on")
            }
            org.jetbrains.compose.web.dom.Text(" where a rejected cache is fatal, and fails unless the configured share of the application's classes came from the cache. The message names which of the three failed and why.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("aotReport")
                }
            }
            org.jetbrains.compose.web.dom.Text(" writes two tables: time to the first ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("200")
            }
            org.jetbrains.compose.web.dom.Text(" cold and cached, medians over sorted runs, and what the JIT still compiles after the start — because the cache holds classes, heap objects and profiles, not compiled code, and a report that showed only the first table would promise a warm service the JVM does not deliver.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("The start scripts pick the cache up")
            }
            org.jetbrains.compose.web.dom.Text(" when it is there and run without it when it is not; ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("distTar")
            }
            org.jetbrains.compose.web.dom.Text(" carries it, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("distZip")
            }
            org.jetbrains.compose.web.dom.Text(" says why it cannot.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A runner for the image.")
            }
            org.jetbrains.compose.web.dom.Text(" Every distribution carries ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("lib/zavarnik-runner.jar")
            }
            org.jetbrains.compose.web.dom.Text(", the same training and verification on a bare JRE with no Gradle and no curl, so the ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-jre")
            }
            org.jetbrains.compose.web.dom.Text(" runtime stage of a Dockerfile trains and verifies the cache on the JVM that will use it.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Portable by default.")
            }
            org.jetbrains.compose.web.dom.Text(" The adapter code the JVM caches for the training CPU is switched off, so a cache trained on a CI runner with AVX-512 does not ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SIGILL")
            }
            org.jetbrains.compose.web.dom.Text(" on a narrower machine; the cost was measured at one millisecond of readiness.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("numbers") }) {
        org.jetbrains.compose.web.dom.Text("Numbers")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In a cluster — konekt, Ktor CIO with Exposed and Postgres under a one-core limit, the cache trained in the release pipeline inside the image that ships, the readiness probe asking every second — the pod is Ready 3 s after its container starts, against 11 s for the release before. On its stand, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docker start")
        }
        org.jetbrains.compose.web.dom.Text(" to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/health")
        }
        org.jetbrains.compose.web.dom.Text(" went from 4.4 s to 2.0 s and the first request from 510 ms to 240 ms at the median of ten restarts; the next hundred requests did not move.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Time to the first ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("200")
        }
        org.jetbrains.compose.web.dom.Text(" on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/health")
        }
        org.jetbrains.compose.web.dom.Text(" of the Ktor sample, ten runs per variant, medians, Linux x86_64, OpenJDK 25.0.4: 143 ms without the cache, 47 ms with it; on macOS arm64 with JetBrains Runtime 25.0.4.1, 98 and 37 ms. The JIT is not warmed: C2 compiles the same thousand-odd methods after the start either way, about ten per cent fewer at best. The cache for the sample is 31 MiB; the container image with a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-jre")
        }
        org.jetbrains.compose.web.dom.Text(" runtime stage is 562 MB against 647 MB with two ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-jdk")
        }
        org.jetbrains.compose.web.dom.Text(" stages.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("requirements") }) {
        org.jetbrains.compose.web.dom.Text("Requirements")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No wildcard on the start script's classpath: the JVM expands ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("lib/*")
        }
        org.jetbrains.compose.web.dom.Text(" in the filesystem's order, which differs between container runtimes, and a cache trained under one is refused under another with nothing on the build machine able to tell. The plugin refuses it at ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("installDist")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("JDK 25 or newer as the toolchain, preferably 25.0.4 or 26.0.2 — the plugin warns on the six builds that skip the jar check and verifies the jars itself on all of them. The ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("application")
        }
        org.jetbrains.compose.web.dom.Text(" plugin. Linux or macOS for training; production on Windows works, training on it does not yet. The same JDK build in production as in training, down to the image.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""// build.gradle.kts
plugins {
    application
    id("io.github.youndie.zavarnik") version "0.1.0"
}

zavarnik {
    training {
        readyWhen.url("http://127.0.0.1:8080/health")
        workload { get("http://127.0.0.1:8080/api/items") }
    }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("0.1.0")
        }
        org.jetbrains.compose.web.dom.Text(" is on the ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://plugins.gradle.org/plugin/io.github.youndie.zavarnik") {
            org.jetbrains.compose.web.dom.Text("Gradle Plugin Portal")
        }
        org.jetbrains.compose.web.dom.Text(", so the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("plugins { }")
        }
        org.jetbrains.compose.web.dom.Text(" block alone installs it — no repository to declare. Every push to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("main")
        }
        org.jetbrains.compose.web.dom.Text(" also publishes a snapshot, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("0.1.0.<run>")
        }
        org.jetbrains.compose.web.dom.Text(", to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("https://reposilite.kotlin.website/snapshots")
        }
        org.jetbrains.compose.web.dom.Text("; the README says how to take one. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("./gradlew check")
        }
        org.jetbrains.compose.web.dom.Text(" then trains and verifies; ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("./gradlew distTar")
        }
        org.jetbrains.compose.web.dom.Text(" ships the cache; the repository's ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("samples/ktor/Dockerfile")
        }
        org.jetbrains.compose.web.dom.Text(" is the container recipe. What the plugin hides — the training, the verification and the packaging, written out as five steps by hand — is ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/leyden-aot-cache-for-a-plain-kotlin-jvm-service") {
            org.jetbrains.compose.web.dom.Text("the how-to")
        }
        org.jetbrains.compose.web.dom.Text("; the JDK builds that accept a stale cache have ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/stale-aot-cache-on-jdk25") {
            org.jetbrains.compose.web.dom.Text("their own post")
        }
        org.jetbrains.compose.web.dom.Text(". A cache is not the only way to start faster: the repository's third phase takes a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("CRaC checkpoint")
        }
        org.jetbrains.compose.web.dom.Text(" of the warmed-up process instead, which restores in 131 ms against 2 317 with its JIT already hot — and what that costs in open sockets is ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/crac-on-a-ktor-service-with-hikari-and-exposed") {
            org.jetbrains.compose.web.dom.Text("every socket that refuses the checkpoint")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The repository also holds a second phase that was measured and not built: a Kotlin bytecode optimiser, closed because user code owns 1–4 % of a Ktor service's CPU — ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/user-code-share-of-a-ktor-service") {
            org.jetbrains.compose.web.dom.Text("the write-up")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
}
